import { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";

const LANGS = [
  { code: "az", label: "AZ" },
  { code: "en", label: "EN" },
  { code: "ru", label: "RU" },
];

const FLAG_SVGS = {
  az: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 20" style={{width:"100%",height:"100%",display:"block"}}>
      <rect width="30" height="6.67" y="0"    fill="#0092BC"/>
      <rect width="30" height="6.67" y="6.67" fill="#E8112D"/>
      <rect width="30" height="6.67" y="13.33" fill="#00B140"/>
      <g fill="#fff" transform="translate(15,10)">
        <path d="M0-4.2a4.2 4.2 0 1 1 0 8.4 3.3 3.3 0 1 0 0-8.4z"/>
        <polygon points="1.8,-1.05 2.7,0 1.8,1.05 3.1,0.6 2.5,1.9 3.6,1.05 3.3,2.5 4.1,1.4 4.1,2.9 4.6,1.7 4.9,3.1 5.1,1.8 5.7,3 5.5,1.7 6.4,2.6 5.9,1.4 7,2 6.1,1 7,0 6.1,-1 7,-2 5.9,-1.4 6.4,-2.6 5.5,-1.7 5.7,-3 5.1,-1.8 4.9,-3.1 4.6,-1.7 4.1,-2.9 4.1,-1.4 3.3,-2.5 3.6,-1.05 2.5,-1.9 3.1,-0.6" transform="scale(0.38) translate(-3,0)"/>
      </g>
    </svg>
  ),
  en: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 20" style={{width:"100%",height:"100%",display:"block"}}>
      <rect width="30" height="20" fill="#B22234"/>
      <rect width="30" height="2.31" y="0"     fill="#B22234"/>
      <rect width="30" height="2.31" y="2.31"  fill="#fff"/>
      <rect width="30" height="2.31" y="4.62"  fill="#B22234"/>
      <rect width="30" height="2.31" y="6.92"  fill="#fff"/>
      <rect width="30" height="2.31" y="9.23"  fill="#B22234"/>
      <rect width="30" height="2.31" y="11.54" fill="#fff"/>
      <rect width="30" height="2.31" y="13.85" fill="#B22234"/>
      <rect width="30" height="2.31" y="16.15" fill="#fff"/>
      <rect width="30" height="2.31" y="18.46" fill="#B22234"/>
      <rect width="12" height="10.77" fill="#3C3B6E"/>
      <g fill="#fff">
        {[0,1,2,3,4].map(row =>
          [0,1,2,3,4,5].slice(0, row%2===0?6:5).map(col => (
            <circle key={`${row}-${col}`}
              cx={row%2===0 ? 1+col*2 : 2+col*2}
              cy={1+row*2}
              r="0.5"
            />
          ))
        )}
      </g>
    </svg>
  ),
  ru: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 20" style={{width:"100%",height:"100%",display:"block"}}>
      <rect width="30" height="6.67" y="0"     fill="#fff"/>
      <rect width="30" height="6.67" y="6.67"  fill="#0039A6"/>
      <rect width="30" height="6.67" y="13.33" fill="#D52B1E"/>
    </svg>
  ),
};



function LangFlag({ code, size = 28 }) {
  return (
    <span style={{
      display: "inline-block",
      width: size,
      height: size * 0.67,
      borderRadius: 3,
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
      flexShrink: 0,
      lineHeight: 0,
    }}>
      {FLAG_SVGS[code]}
    </span>
  );
}

function LangDropdown({ lang, scrolled, handleLangChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const current = LANGS.find(l => l.code === lang) || LANGS[0];
  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(v => !v)}
        style={{ display:"flex", alignItems:"center", gap:6, padding:"5px 10px", borderRadius:8, border: scrolled ? "1px solid #e5e7eb" : "1px solid rgba(255,255,255,0.3)", background: scrolled ? "#f9fafb" : "rgba(255,255,255,0.15)", cursor:"pointer", transition:"all 0.2s" }}
      >
        <LangFlag code={current.code} size={26} />
        <span style={{ fontSize:11, fontWeight:700, color: scrolled ? "#374151" : "#fff", letterSpacing:"0.5px" }}>{current.label}</span>
        <ChevronDown style={{ width:11, height:11, color: scrolled ? "#6b7280" : "rgba(255,255,255,0.8)", transform: open ? "rotate(180deg)" : "none", transition:"transform 0.2s" }} />
      </button>
      {open && (
        <div style={{ position:"absolute", right:0, top:"calc(100% + 8px)", background:"#fff", borderRadius:12, boxShadow:"0 8px 32px rgba(0,0,0,0.15)", border:"1px solid #f1f5f9", overflow:"hidden", zIndex:50, minWidth:120 }}>
          {LANGS.map(({ code, label }) => (
            <button
              key={code}
              onClick={() => { handleLangChange(code); setOpen(false); }}
              style={{ width:"100%", display:"flex", alignItems:"center", gap:10, padding:"9px 14px", background: lang===code ? "#1e3a8a" : "transparent", border:"none", cursor:"pointer", transition:"background 0.15s" }}
              onMouseEnter={e => { if (lang!==code) e.currentTarget.style.background="#eff6ff"; }}
              onMouseLeave={e => { if (lang!==code) e.currentTarget.style.background="transparent"; }}
            >
              <LangFlag code={code} size={26} />
              <span style={{ fontSize:13, fontWeight:600, color: lang===code ? "#fff" : "#374151" }}>{label}</span>
              {lang === code && <span style={{ marginLeft:"auto", color:"#93c5fd", fontSize:11 }}>✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


export { LangFlag, LangDropdown };
